"use client";

import type { TeacherWrongSubmission } from "../../../services/backend/api";

type WrongSubmissionsAnalysisProps = {
  isVisible: boolean;
  submissions: TeacherWrongSubmission[];
};

const formatAttemptType = (type: TeacherWrongSubmission["attemptType"]): string =>
  type === "drawing" ? "Drawing" : "Pronunciation";

const formatTimestamp = (timestamp: string): string => {
  const parsed = Date.parse(timestamp);
  if (Number.isNaN(parsed)) return timestamp;
  return new Date(parsed).toLocaleDateString();
};

const WrongSubmissionsAnalysis = ({
  isVisible,
  submissions,
}: WrongSubmissionsAnalysisProps) => {
  return (
    <div
      className={`overflow-hidden rounded-2xl border border-[#d9caeb] bg-white/45 shadow-sm backdrop-blur-lg transition-all duration-700 ${
        isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
      }`}
    >
      <div className="border-b border-[#dccdf0] bg-white/40 px-4 py-3">
        <h2 className="text-lg font-bold text-[#5f438a]">Wrong Submissions</h2>
        <p className="text-sm font-medium text-[#8f7bb4]">
          Shows pronunciation and drawing attempts marked as wrong.
        </p>
      </div>

      <div className="p-3 md:p-4">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[920px] text-sm">
            <thead>
              <tr className="border-b border-[#dccdf0] text-[#7b649f]">
                <th className="px-3 py-3 text-left font-semibold">Student</th>
                <th className="px-3 py-3 text-center font-semibold">Roll No</th>
                <th className="px-3 py-3 text-center font-semibold">Grade</th>
                <th className="px-3 py-3 text-center font-semibold">Section</th>
                <th className="px-3 py-3 text-center font-semibold">Attempt</th>
                <th className="px-3 py-3 text-left font-semibold">Word</th>
                <th className="px-3 py-3 text-left font-semibold">Local Word</th>
                <th className="px-3 py-3 text-left font-semibold">Category</th>
                <th className="px-3 py-3 text-center font-semibold">Date</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((submission) => (
                <tr
                  key={submission.id}
                  className="border-b border-[#eadff8]/70 text-[#6b558f] transition hover:bg-white/40"
                >
                  <td className="px-3 py-3 font-semibold text-[#57417d]">
                    {submission.studentName}
                  </td>
                  <td className="px-3 py-3 text-center font-medium">{submission.rollNo}</td>
                  <td className="px-3 py-3 text-center font-medium">{submission.classId}</td>
                  <td className="px-3 py-3 text-center font-medium">{submission.section}</td>
                  <td className="px-3 py-3 text-center">
                    <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-bold text-red-700">
                      {formatAttemptType(submission.attemptType)}
                    </span>
                  </td>
                  <td className="px-3 py-3 font-medium">{submission.wordEnglish}</td>
                  <td className="px-3 py-3 font-medium">{submission.wordLocal}</td>
                  <td className="px-3 py-3 font-medium">{submission.category}</td>
                  <td className="px-3 py-3 text-center text-xs font-medium md:text-sm">
                    <span className="rounded-lg bg-white/80 px-2 py-1 text-[#6d5b8b]">
                      {formatTimestamp(submission.timestamp)}
                    </span>
                  </td>
                </tr>
              ))}

              {submissions.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className="px-3 py-8 text-center text-sm font-medium text-[#8f7bb4]"
                  >
                    No wrong pronunciation or drawing submissions found for selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WrongSubmissionsAnalysis;
